/**
 * Data & Privacy — GDPR / CCPA controls (Aug 2026 mobile parity).
 *   • Export (async ZIP within 48h)
 *   • Purge (erase data, keep account — 30-day rate limit)
 *   • Ad preferences (personalized ads · activity for recommendations)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Download, Eraser, Sparkles, Loader2 } from 'lucide-react';

const Toggle = ({ on, onClick, testId, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? 'bg-[#B91C1C]' : 'bg-black/[0.15]'} ${disabled ? 'opacity-40' : ''}`}
    aria-pressed={on}>
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
  </button>
);

export default function DataPrivacy() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get('/me/ad-prefs').then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  const togglePref = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(key);
    try {
      const r = await api.put('/me/ad-prefs', { [key]: next[key] });
      setPrefs(r.data);
    } catch {
      setPrefs(prefs);
      toast.error('Save failed');
    } finally { setSaving(null); }
  };

  const requestExport = async () => {
    setBusy('export');
    try {
      const r = await api.post('/me/export');
      toast.success(r.data.message || 'Export queued');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const requestPurge = async () => {
    if (!window.confirm("Erase your messages, matches, and photos? Your account stays. This can't be undone.")) return;
    setBusy('purge');
    try {
      const r = await api.post('/me/purge');
      toast.success(r.data.message || 'Purge queued');
    } catch (e) {
      if (e.response?.status === 429) toast.error(e.response.data.detail);
      else toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="data-privacy-page">
      <header className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="data-back" aria-label="Back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">SUPPORT & LEGAL</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Data & Privacy</h1>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 space-y-3">
        <button
          onClick={requestExport}
          disabled={busy === 'export'}
          data-testid="data-export-btn"
          className="w-full flex items-center gap-3 py-4 px-4 rounded-2xl bg-white border border-[var(--hairline)] text-left hover:bg-black/[0.02] transition disabled:opacity-50"
        >
          <div className="w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0">
            {busy === 'export' ? <Loader2 size={16} className="animate-spin"/> : <Download size={17} className="text-[#0F0F10]" />}
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-[#0F0F10]">Download my data</p>
            <p className="text-[12px] text-[#8A8A93]">We'll email a ZIP within 48 hours.</p>
          </div>
        </button>

        <button
          onClick={requestPurge}
          disabled={busy === 'purge'}
          data-testid="data-purge-btn"
          className="w-full flex items-center gap-3 py-4 px-4 rounded-2xl bg-white border border-[var(--hairline)] text-left hover:bg-black/[0.02] transition disabled:opacity-50"
        >
          <div className="w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0">
            {busy === 'purge' ? <Loader2 size={16} className="animate-spin"/> : <Eraser size={17} className="text-[#B91C1C]" />}
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-[#0F0F10]">Erase my activity</p>
            <p className="text-[12px] text-[#8A8A93]">Delete messages, matches, and photos. Account stays. Once every 30 days.</p>
          </div>
        </button>

        {/* Ad prefs */}
        {prefs && (
          <div className="pt-2 space-y-3" data-testid="data-ad-prefs-block">
            <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#8A8A93] px-1 pt-2">AD PREFERENCES</p>
            <div className="flex items-center gap-3 py-4 px-4 rounded-2xl bg-white border border-[var(--hairline)]">
              <div className="w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0">
                <Sparkles size={17} className="text-[#0F0F10]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#0F0F10]">Personalized ads</p>
                <p className="text-[12px] text-[#8A8A93]">Show ads tailored to my activity.</p>
              </div>
              <Toggle on={prefs.personalized_ads_opt_in}
                onClick={() => togglePref('personalized_ads_opt_in')}
                testId="data-personalized-ads-toggle"
                disabled={saving === 'personalized_ads_opt_in'} />
            </div>
            <div className="flex items-center gap-3 py-4 px-4 rounded-2xl bg-white border border-[var(--hairline)]">
              <div className="w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0">
                <Sparkles size={17} className="text-[#0F0F10]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#0F0F10]">Use activity for recommendations</p>
                <p className="text-[12px] text-[#8A8A93]">Help us surface better matches.</p>
              </div>
              <Toggle on={prefs.activity_for_recommendations_opt_in}
                onClick={() => togglePref('activity_for_recommendations_opt_in')}
                testId="data-activity-recs-toggle"
                disabled={saving === 'activity_for_recommendations_opt_in'} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
